import React, { useState } from 'react';
import { evaluateKeyStrength } from '../utils/encryption';

function EncryptionForm({ onEncrypt, onCancel }) {
  const [key, setKey] = useState('');
  const [keyStrength, setKeyStrength] = useState({ level: 0, strengthText: '', feedback: '' });
  const [error, setError] = useState('');

  const handleKeyChange = (e) => {
    const newKey = e.target.value;
    setKey(newKey);
    setKeyStrength(evaluateKeyStrength(newKey));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (key.length < 4) {
      setError('The key must be at least 4 characters long');
      return;
    }
    onEncrypt(key);
  };

  // Color based on key strength
  const getStrengthColor = () => {
    switch (keyStrength.level) {
      case 1: return '#FF4136'; // Very Weak - Red
      case 2: return '#FF851B'; // Weak - Orange
      case 3: return '#FFDC00'; // Moderate - Yellow
      case 4: return '#2ECC40'; // Strong - Green
      case 5: return '#01FF70'; // Very Strong - Bright Green
      default: return '#AAAAAA'; // Grey
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>Secure Your Documentation</h2>
      </div>
      <div className="card-body">
        <div className="security-info">
          <p>
            Your documentation will be encrypted using AES-256 encryption, which is a highly secure method
            used by governments and organizations to protect sensitive information.
          </p>
          
          <h3 className="mt-3">Why Encryption Matters</h3>
          <p>
            Encrypting your documentation helps ensure that only you or trusted individuals with the key
            can access the sensitive information. This is particularly important for:
          </p>
          <ul className="security-benefits">
            <li>Protecting your privacy if your device is lost or accessed by others</li>
            <li>Securely storing documentation for potential legal proceedings</li>
            <li>Maintaining control over who can access your personal information</li>
            <li>Creating secure backups of important documentation</li>
          </ul>
        </div>
        
        <div className="key-instructions mt-4">
          <h3>Creating Your Secure Key</h3>
          <p>
            Your encryption key is like a password that will be needed to unlock your documentation later.
            Choose a key that is:
          </p>
          <ul>
            <li>Memorable to you but not easily guessed by others</li>
            <li>Not used for other accounts or services</li>
            <li>A combination of letters, numbers, and special characters for best security</li>
            <li>At least 8-12 characters long for strong protection</li>
          </ul>
          <p className="important-note">
            <strong>Important:</strong> This key cannot be recovered if forgotten. Please store it in a secure 
            location or with a trusted person if needed for future access.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="form-group">
            <label className="form-label">Enter Your Encryption Key:</label>
            <input
              type="password"
              value={key}
              onChange={handleKeyChange}
              className="form-control"
              placeholder="Enter a strong, memorable encryption key"
            />
            
            {key && (
              <div className="key-strength-meter mt-2">
                <div className="strength-bar-container">
                  <div 
                    className="strength-bar" 
                    style={{ 
                      width: `${(keyStrength.level / 5) * 100}%`,
                      backgroundColor: getStrengthColor()
                    }}
                  ></div>
                </div>
                <div className="strength-text" style={{ color: getStrengthColor() }}>
                  {keyStrength.strengthText} 
                  {keyStrength.feedback && (
                    <span className="strength-feedback"> - {keyStrength.feedback}</span>
                  )}
                </div>
              </div>
            )}
            
            {error && <div className="error-message">{error}</div>}
          </div>
          
          <div className="button-group mt-4">
            <button 
              type="button" 
              className="btn btn-secondary mr-3"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={key.length < 4}
            >
              Secure My Documentation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EncryptionForm;